import { useEffect, useState } from "react";
import { escapeRegExp, filter, get, isEmpty, join, kebabCase, startCase } from "lodash";
import { Grid2, Pagination, Table, TableContainer, SxProps, TableBody } from "@mui/material";
import { Order } from "../../../../../typings/types";
import {
  customTableHeader,
  EnhancedTableHead,
  TableToolbar,
} from "../../../custom-components/table-helper/helperTable";
import SurfaceCard from "@components/shared/ui/SurfaceCard";

import { AssessmentTableRows } from "../@dashboard/@assesments/AssessmentTableRows";
import { StudentProgressTableRows } from "../@dashboard/@progress/StudentProgressTableRows";
import { StudentPerformanceTableRows } from "../@dashboard/@performance/StudentPerformanceTableRows";
import { CoursesProgressTableRow } from "../@dashboard/@studentProfile/analyticsTabs/CoursesProgressTableRow";
import { StudentProgressPerformanceTableRows } from "../@dashboard/StudentProgressPerformanceTableRows";
import { StudentTableRows } from "../@students/StudentTableRows";

// Stable empty-array reference for the `searchBy` default so it can safely be
// an effect dependency without changing identity on every render.
const EMPTY_SEARCH_BY: string[] = [];

interface Props {
  title:
    | "students"
    | "students-progress"
    | "progress-and-performance"
    | "assigned-assesments"
    | "grade-book"
    | "courses"
    | "catalogs"
    | "catalogs-courses"
    | "catalog-courses"
    | "course-catalog"
    | "course-request"
    | "resources"
    | "participants"
    | "assign-courses"
    | "students-performance"
    | "courses-progress"
    | "resent-activities"
    | "pending-lessons"
    | "admin-roles";
  column: string[];
  rows: any[];
  pagination?: boolean;
  totalPage?: number;
  header?: boolean;
  searchBar?: boolean;
  searchBy?: string[];
  sx?: SxProps;
  size?: "small" | "medium";
  propertyMap?: { [key: string]: string };
}

const TeacherDataTable = ({
  rows,
  title,
  column,
  sx,
  size,
  totalPage, // hide pagination if not define
  header = true, // hide/show header
  searchBy = EMPTY_SEARCH_BY, // ["attributes.first_name", "attributes.last_name"]
  propertyMap, // use for sorting [nested data]
  // propertyMap = {
  //    name: "attributes.first_name",
  //    ...
  // },
}: Props) => {
  const [page, setPage] = useState(1);

  const [order, setOrder] = useState<Order>("asc");
  const [orderBy, setOrderBy] = useState<string>("");

  const [searchInput, setSearchInput] = useState("");
  const [searchResult, setSearchResult] = useState([]);

  const onSelectPage = (_e: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  useEffect(() => {
    const regex = new RegExp(escapeRegExp(searchInput), "gi");

    const filteredResult: any = filter(rows, (r) => {
      const targetCombined = join(searchBy?.map((s) => get(r, s)));

      return regex.test(targetCombined);
    });

    setSearchResult(filteredResult);
    // Recompute when the data (rows) or search columns change too, not only when
    // the search box changes — otherwise the view goes stale once rows load.
  }, [searchInput, rows, searchBy]);

  const handleRequestSort = (_e: React.MouseEvent<unknown>, property: string) => {
    const sorting = propertyMap?.[property] || property;

    // Check if the current sort is ascending
    const isAsc = orderBy === sorting && order === "asc";

    // Toggle the sort order and set the sorting field
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(sorting as Order);
  };

  const type = kebabCase(title);

  const defaultState = [{ id: null, status: "LOADING..." }];
  const data = searchInput ? searchResult : rows || defaultState;

  return (
    <Grid2 sx={{ width: "100%" }}>
      <SurfaceCard sx={{ width: "100%", mb: 2, p: 2, pb: 0, borderRadius: 3, ...sx }}>
        {header && (
          <TableToolbar
            searchBy={searchBy}
            search={searchInput}
            title={startCase(title)}
            setSearch={setSearchInput}
            searchPlaceHolder={startCase(column?.[0])}
          />
        )}

        <TableContainer>
          <Table sx={{ width: "100%" }} size={size}>
            <EnhancedTableHead
              disableSort={isEmpty(propertyMap)}
              column={customTableHeader(column)}
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
            />
            {/* CUSTOMIZE row being rendered */}
            {["courses-progress"].includes(type) && <CoursesProgressTableRow rows={data} />}

            {["students-progress"].includes(type) && (
              <StudentProgressTableRows rows={data} order={order as Order} orderBy={orderBy} />
            )}

            {type === "students" && (
              <StudentTableRows rows={data} order={order as Order} orderBy={orderBy} />
            )}

            {type === "students-performance" && (
              <StudentPerformanceTableRows rows={data} order={order} orderBy={orderBy} />
            )}

            {type === "progress-and-performance" && (
              <StudentProgressPerformanceTableRows rows={data} order={order} orderBy={orderBy} />
            )}

            {["assigned-assesments"].includes(type) && (
              <AssessmentTableRows rows={data} order={order} orderBy={orderBy} />
            )}

            {/* {["resources"].includes(type) && (
              <ResourcesTableRow rows={data} order={order} orderBy={orderBy} />
            )} */}

            <TableBody id="datatable-row" />
          </Table>
        </TableContainer>

        {/* PAGINATION */}
        {totalPage ? (
          <Grid2 container py={2} justifyContent="end">
            <Pagination
              page={page}
              count={totalPage || 1}
              onChange={onSelectPage}
              shape="rounded"
              color="primary"
              variant="outlined"
            />
          </Grid2>
        ) : (
          <Grid2 pb={2} />
        )}
      </SurfaceCard>
    </Grid2>
  );
};

export default TeacherDataTable;
